import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Ticket, ArrowRight, Loader2, AlertTriangle, Clock, CheckCircle2, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * /recover-pass/:token — lightweight magic-link landing page.
 *
 * Responsibilities:
 *   - Validate the token against the backend exactly once.
 *   - If exactly ONE pass is returned, auto-redirect to /ticket/:id.
 *   - If MULTIPLE passes, render a minimal picker — each row links to
 *     /ticket/:id (TicketDisplay remains the final pass view).
 *   - If invalid / expired, show an error state with a path back to /find-pass.
 */
function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

const STATUS_META = {
  access_granted: { label: 'Active',           color: 'text-emerald-400', icon: CheckCircle2 },
  used:           { label: 'Checked In',       color: 'text-[#9EA5B0]',   icon: CheckCircle2 },
  pending_approval: { label: 'Pending Approval', color: 'text-[#FFB800]',   icon: Clock },
  revoked:        { label: 'Revoked',          color: 'text-[#C44545]',   icon: XCircle },
};

export default function RecoverPass() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setError('Invalid link');
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/pass-recovery/validate/${encodeURIComponent(token)}`);
        if (cancelled) return;
        const list = Array.isArray(res.data?.tickets) ? res.data.tickets : [];
        const ids = Array.isArray(res.data?.ticket_ids) ? res.data.ticket_ids : [];

        // Single-pass case: redirect straight to the final pass view.
        if (list.length === 1) {
          navigate(`/ticket/${list[0].ticket_id}`, { replace: true });
          return;
        }
        if (list.length === 0 && ids.length === 1) {
          navigate(`/ticket/${ids[0]}`, { replace: true });
          return;
        }

        setTickets(list.length ? list : ids.map((ticket_id) => ({ ticket_id })));
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || 'This link is invalid or has expired';
        setError(detail);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-[#050505] text-[#EAE7E1]" data-testid="recover-pass-page">
      <header className="border-b border-[#1A1A1A]">
        <div className="max-w-[560px] mx-auto px-5 sm:px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-sm tracking-[0.4em] font-bold text-[#D4AF37] uppercase" data-testid="recover-pass-home-link">VelvetGate</Link>
          <Link to="/" className="text-xs text-[#777] hover:text-[#EAE7E1] transition-colors">← Home</Link>
        </div>
      </header>

      <main className="max-w-[560px] mx-auto px-5 sm:px-6 py-10 sm:py-14">
        {loading && (
          <div className="flex flex-col items-center text-center py-16" data-testid="recover-pass-loading">
            <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin mb-3" />
            <p className="text-sm text-[#B9B1A6]">Verifying your link&hellip;</p>
          </div>
        )}

        {!loading && error && (
          <div data-testid="recover-pass-error-state">
            <div className="text-[10px] tracking-[0.35em] text-[#C44545] uppercase font-semibold mb-3">
              Pass Recovery
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#F3EEE7] mb-3" data-testid="recover-pass-error-title">
              Link Invalid or Expired
            </h1>
            <div className="flex items-start gap-3 border border-[#C44545]/25 bg-[#C44545]/5 rounded-sm p-4 mb-6">
              <AlertTriangle className="w-5 h-5 text-[#C44545] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#B9B1A6] leading-relaxed" data-testid="recover-pass-error-message">
                {error}. Recovery links are valid for 30 minutes for your security.
              </p>
            </div>
            <Link
              to="/find-pass"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] px-5 py-3 rounded-sm font-bold text-sm transition-opacity"
              data-testid="recover-pass-request-new"
            >
              Request a new link
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-xs text-[#777] mt-4">
              Need help? <Link to="/contact" className="text-[#D4AF37] hover:underline">Contact VelvetGate</Link>.
            </p>
          </div>
        )}

        {!loading && !error && tickets.length > 0 && (
          <div data-testid="recover-pass-picker">
            <div className="text-[10px] tracking-[0.35em] text-[#D4AF37] uppercase font-semibold mb-3">
              Pass Recovery
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#F3EEE7] mb-2" data-testid="recover-pass-title">
              Choose a Pass
            </h1>
            <p className="text-sm text-[#B9B1A6] mb-6" data-testid="recover-pass-subtitle">
              We found {tickets.length} {tickets.length === 1 ? 'pass' : 'passes'} on this link. Select one to view.
            </p>

            <ul className="space-y-2" data-testid="recover-pass-list">
              {tickets.map((t) => {
                const meta = STATUS_META[t.status] || null;
                const StatusIcon = meta?.icon;
                return (
                  <li key={t.ticket_id}>
                    <Link
                      to={`/ticket/${t.ticket_id}`}
                      className="group flex items-center gap-3 p-4 border border-[#1A1A1A] rounded-sm bg-[#0A0A0A] hover:border-[#D4AF37]/40 hover:bg-[#0D0D0D] transition-colors"
                      data-testid={`recover-pass-item-${t.ticket_id}`}
                    >
                      <div className="w-10 h-10 rounded-sm bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <Ticket className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#F3EEE7] truncate" data-testid="recover-pass-item-event">
                          {t.event_title || 'Event'}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#777] mt-0.5">
                          {t.event_date && <span>{formatDate(t.event_date)}</span>}
                          {t.tier_name && <><span className="text-[#2A2A2A]">·</span><span className="text-[#B9B1A6]">{t.tier_name}</span></>}
                          {meta && (
                            <>
                              <span className="text-[#2A2A2A]">·</span>
                              <span className={`inline-flex items-center gap-1 ${meta.color}`}>
                                {StatusIcon && <StatusIcon className="w-3 h-3" />}
                                {meta.label}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#555] group-hover:text-[#D4AF37] transition-colors flex-shrink-0" />
                    </Link>
                  </li>
                );
              })}
            </ul>

            <p className="text-[11px] text-[#555] mt-6">
              This link expires 30 minutes after it was requested.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
